import { useEffect, useMemo, useState } from 'react'
import { useAccount, usePublicClient, useReadContract, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import protectionAbi from '@/lib/abis/ProtectionManager.json'
import { ERC20_ABI } from '@/lib/erc20'
import { getLogsChunked } from '@/lib/getLogsChunked'
import { POLL_SLOW } from '@/lib/utils'

const SCALE = 10n ** 18n

/// Contract constants, mirrored so the form can validate BEFORE a signature rather than surfacing a
/// `ParamOutOfRange` after one. Kept here next to the reads they constrain, and asserted against the
/// on-chain values by useProtection.test.js - a mirrored constant that silently disagrees with the
/// contract is worse than no mirror at all.
export const MIN_TRIGGER_HF = 1.01
export const MIN_TARGET_GAP = 0.02
export const MAX_SLIPPAGE_BPS = 1000

/// The recommended trigger, and the reason it is not the tightest one available.
///
/// A tighter trigger means the protection fires closer to liquidation, which sounds safer and is
/// not: the repay needed to reach any given target grows as health falls, so a trigger of 1.05
/// authorises a LARGER collateral sale under WORSE conditions than a trigger of 1.15 does. The
/// contract permits anything from 1.01 up; this is the default the form starts on.
export const DEFAULT_TRIGGER_HF = 1.15
export const DEFAULT_TARGET_HF = 1.35

export const hfToWei = (n) => BigInt(Math.round(n * 1e4)) * 10n ** 14n
export const weiToHf = (x) => (x == null ? null : Number(x) / 1e18)

const manager = ADDRESSES.PROTECTION_MANAGER
const NOT_DEPLOYED = !IS_LIVE || !manager || manager.toLowerCase() === ZERO_ADDR

const KEEPER_SET = protectionAbi.find((x) => x.type === 'event' && x.name === 'KeeperSet')

/// Whether ANY keeper is allowlisted, which is the fifth condition and the one with no cheap read.
///
/// It matters because it is the difference between a setting and a defence: with the toggle on, the
/// approval given and the venue live, a position with no keeper watching is still exactly as
/// liquidatable as an unprotected one. Claiming "Active" in that state would be the precise failure
/// the four separate flags exist to prevent, one step further out.
///
/// `isKeeper` is a per-address read and the contract holds no roster, deliberately, so this replays
/// `KeeperSet` instead. Scanned only when the caller asks (the other three conditions already hold),
/// since that is the rare path and the only one where the answer changes what the UI may claim.
/// **On failure this returns `null` rather than `true` or `false`** - an RPC that refuses a log scan
/// is an unknown, and an unknown must not be rendered as a guarantee.
function useAnyKeeper(enabled) {
  const client = usePublicClient()
  const [known, setKnown] = useState(null)

  useEffect(() => {
    if (NOT_DEPLOYED || !enabled || !client || !KEEPER_SET) { setKnown(null); return undefined }
    let cancelled = false

    ;(async () => {
      try {
        const fromBlock = ADDRESSES.LENDING_POOL_DEPLOY_BLOCK
          ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK)
          : 0n
        const logs = await getLogsChunked(client, { address: manager, event: KEEPER_SET, fromBlock })
        // Replayed in order: an address can be added and later removed, so the LAST entry per
        // address wins. Counting `allowed: true` events alone would keep reporting a keeper that
        // genesis has since revoked.
        const state = new Map()
        for (const log of logs) state.set(log.args?.keeper, Boolean(log.args?.allowed))
        if (!cancelled) setKnown([...state.values()].some(Boolean))
      } catch {
        if (!cancelled) setKnown(null)
      }
    })()

    return () => { cancelled = true }
  }, [client, enabled])

  return known
}

/// Everything the protection UI needs for ONE collateral/debt pair, in one place.
///
/// **Four separate things have to be true before protection actually works**, and collapsing them
/// into a single boolean is how a toggle ends up claiming a position is defended when it is not:
///
///   1. The user enabled it (`settingsOf.enabled`) - their trigger and target.
///   2. They approved this contract to move that reserve's sToken. **This is the real
///      authorisation.** `disable` is one revocation path; the approval is the other and the
///      stronger one, because revoking it severs the contract regardless of what its own state says.
///      A UI that shows only the toggle implies the toggle is the whole story.
///   3. Genesis configured a swap venue for the pair. Without one `protect` reverts
///      `SwapPoolNotConfigured` - so an enabled position on an unconfigured pair is enabled and
///      undefended, which is the exact state worth naming out loud.
///   4. That venue has AGED past `minPoolAgeSeconds`. `useLoops` learned this the hard way: a
///      correctly-pointed venue still refuses to swap until its observation buffer holds something
///      worth averaging, and treating "configured" as "live" produces a UI that offers a protection
///      which cannot execute. Same gate, same read, same reason.
///   5. A keeper is allowlisted to actually call it - see `useAnyKeeper`. This one can come back
///      UNKNOWN, and unknown is reported as unknown rather than folded into either answer.
///
/// Returns them separately so the UI can say which one is missing.
export function useProtection({ collateralAsset, debtAsset, sTokenAddress } = {}) {
  const { address: user } = useAccount()

  const pairReady = !NOT_DEPLOYED && !!collateralAsset && !!debtAsset

  const { data: pairReads, refetch: refetchPair } = useReadContracts({
    query: { enabled: pairReady, refetchInterval: POLL_SLOW },
    contracts: pairReady ? [
      {
        address: manager, abi: protectionAbi, functionName: 'settingsOf',
        args: [user ?? ZERO_ADDR, collateralAsset, debtAsset],
      },
      // Both orders, because the contract's own `_venue` falls back to the reverse key - genesis
      // may have configured the pair either way round and the UI must agree with the contract about
      // whether a venue exists.
      { address: manager, abi: protectionAbi, functionName: 'swapPools', args: [collateralAsset, debtAsset] },
      { address: manager, abi: protectionAbi, functionName: 'swapPools', args: [debtAsset, collateralAsset] },
    ] : [],
  })

  const { data: minPoolAge } = useReadContract({
    address: manager, abi: protectionAbi, functionName: 'minPoolAgeSeconds',
    query: { enabled: !NOT_DEPLOYED, refetchInterval: POLL_SLOW },
  })

  const { data: allowanceWei, refetch: refetchAllowance } = useReadContract({
    address: sTokenAddress, abi: ERC20_ABI, functionName: 'allowance',
    args: user && sTokenAddress ? [user, manager] : undefined,
    query: { enabled: !NOT_DEPLOYED && !!user && !!sTokenAddress, refetchInterval: POLL_SLOW },
  })

  const base = useMemo(() => {
    if (NOT_DEPLOYED) {
      return { deployed: false, enabled: false, venueConfigured: false, venueReady: false, approved: false }
    }

    const s = pairReads?.[0]?.result
    const enabled = s ? Boolean(s[0] ?? s.enabled) : false
    const triggerHf = s ? weiToHf(s[1] ?? s.triggerHf) : null
    const targetHf = s ? weiToHf(s[2] ?? s.targetHf) : null
    const slippageBps = s ? Number(s[3] ?? s.slippageBps ?? 0) : 0

    // Forward order first, then the reverse - the same precedence `_venue` applies.
    const forward = pairReads?.[1]?.result
    const reverse = pairReads?.[2]?.result
    const pick = (v) => (v && (v[0] ?? v.pool) && (v[0] ?? v.pool) !== ZERO_ADDR ? v : null)
    const venue = pick(forward) ?? pick(reverse)
    const venueConfigured = !!venue
    const setAt = venue ? BigInt(venue[2] ?? venue.setAt ?? 0n) : 0n
    const readyAt = venue ? setAt + BigInt(minPoolAge ?? 0) : 0n
    const venueReady = venueConfigured && BigInt(Math.floor(Date.now() / 1000)) >= readyAt

    // A non-zero allowance is not enough to call it approved: a protection sells a slice sized to
    // the position, so an allowance smaller than the user's whole sToken balance can cover today's
    // protection and fail tomorrow's silently. Treated as approved only at effectively-unlimited,
    // which is also what the approve button sends.
    const approved = (allowanceWei ?? 0n) > SCALE * 10n ** 12n

    return {
      deployed: true,
      user,
      enabled,
      triggerHf,
      targetHf,
      slippageBps,
      venueConfigured,
      venueReady,
      venueReadyAt: readyAt,
      allowanceWei: allowanceWei ?? 0n,
      approved,
      // The first four conditions. `active` is assembled below, once the fifth is known, and is the
      // only field a caller should gate a "you are protected" claim on.
      configured: enabled && approved && venueReady,
      refetch: () => { refetchPair?.(); refetchAllowance?.() },
    }
  }, [pairReads, minPoolAge, allowanceWei, user, refetchPair, refetchAllowance])

  // Scanned only once the other four hold - that is both the rare path and the only one where the
  // answer changes what the UI is entitled to claim.
  const anyKeeper = useAnyKeeper(base.configured)

  return useMemo(() => ({
    ...base,
    anyKeeper,
    // `active` requires the keeper question to have been ANSWERED, so an unknown reads as
    // not-yet-active rather than as active. The cost of being wrong is asymmetric: understating
    // sends a user to check, overstating tells them a position is defended when it may not be.
    active: base.configured && anyKeeper === true,
  }), [base, anyKeeper])
}
